/* eslint-disable import/no-extraneous-dependencies */
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import SwipeableViews from 'react-swipeable-views';
import { virtualize, bindKeyboard } from 'react-swipeable-views-utils';
import { TextField, ThemeOptions, Theme, SelectChangeEvent, Autocomplete, useTheme, Grid, Typography, Switch } from '@mui/material';
import { makeStyles } from '@mui/styles';
import INTAKTLogo from '@/assets/images/intakt_logo.png';
import ZASMLogo from '@/assets/images/zasm_logo.png';
import HighlightMask from '@/components/HighlightMask/HighlightMask';
import SubCampaignCRSkeleton from '@/components/Skeletons/SubCampaignCRSkeleton';
import ZasmConnectionButton from '@/components/SubCampaignHeader/ZasmConnectionButton/ZasmConnectionButton';
import TabLabelIcon from '@/components/TabLabel/TabLabelIcon';
import AccordionPanel from '@/components/UI/AccordionPanel/AccordionPanel';
import Button from '@/components/UI/Button/Button';
import ConfirmDialog from '@/components/UI/Dialog/ConfirmDialog';
import InputText from '@/components/UI/InputText/InputText';
import MultiSelection from '@/components/UI/MultiSelection/MultiSelection';
import Spinner from '@/components/UI/Spinner/Spinner';
import TooltipComponent from '@/components/UI/Tooltip/TooltipComponent';
import { WORKFLOW_TYPE, fieldTypes, errorTypes, HELP_AREA } from '@/helpers/constants';
import { scrollToField } from '@/helpers/utils';
import useApiRequest from '@/hooks/useApiRequest';
import useAppLanguage from '@/hooks/useAppLanguage';
import useAuth from '@/hooks/useAuth';
import { getTabIndex } from '@/hooks/useCampaignSearchSelected';
import useCheckDisabled from '@/hooks/useCheckDisabled';
import useError from '@/hooks/useError';
import useSubCampaignValidate from '@/hooks/useSubCampaignValidate';
import useToastContext from '@/hooks/useToastContext';
import { setHighlightedTab } from '@/redux/Campaign/campaign.action';
import { getCampaignSearch, getHighlightTab, getIsVANCR } from '@/redux/Campaign/campaign.reducer';
import { setAreaHelp } from '@/redux/Help/help.action';
import {
  setFeedbackValidations,
  setSubCampaignFeedback, setSubCampaignOriginalValues,
} from '@/redux/SubCampaign/subCampaign.action';
import {
  getSubCampaignFeedback, getSubCampaignOriginalValues, getSubCampaignInfo, getFeedbackValidations,
} from '@/redux/SubCampaign/subCampaign.reducer';
import { setSettingsInfo } from '@/redux/User/user.action';
import { getSettingsInfo } from '@/redux/User/user.reducer';
import campaignServices from '@/services/campaignServices';
import usersServices from '@/services/usersServices';
import {
  CampaignOfFeedbackSubCR, CampaignSearch, FeedbackCustomForm, ReduxForms, ReduxFormsState, SubCampaignFeedback,
  SubCampaignInfo, User, UserSettings, LanguageValidations,
} from '@/types';
import { OriginalValues } from '@/types/Campaign';
import useQuery from '@/utils/hooks';
import AditionalInfoSection from './AditionalInfoSection';
import ApproverCommentSection from './ApproverCommentSection';
import { EmptySubCRCampaign, buildExternalUrls, fieldIsValid, getZasmCampaignState, isZASMUrls } from './feedback-utils';
import FeedbackVehiclesTable from './FeedbackVehiclesTable';
import TabsCampaignsSubCR from './TabsCampaignsSubCR';
import TimeBillingSection from './TimeBillingSection';
import TitleWithDate from './TitleWithDate';
import styles from './Feedback.module.scss';

const VirtualizeSwipeableViews = bindKeyboard(virtualize(SwipeableViews));
const NormalA = ({ children, ...props }) => <a className={styles.NormalA} target="_blank" rel="noreferrer" {...props}>{children}</a>;

const useStyles = makeStyles((theme: ThemeOptions & Theme) => ({
  chip: { borderRadius: 4, display: 'flex', justifyContent: 'space-between', width: '120px' },
  disableInputRoot: { backgroundColor: theme.palette.grey[100] },
  fullWidth: { width: '100%' },
}));

type FeedbackProps = {
  setFeedback: (feedback: SubCampaignFeedback) => void;
  originalValues: OriginalValues;
  setOriginalValues: (originalValues: OriginalValues) => void;
  subCampaignInfo: SubCampaignInfo;
  subCampaignFeedback: SubCampaignFeedback;
  setHelpArea: (helpArea: string) => void;
  isVANCR: boolean;
  validations: LanguageValidations;
  campaignSearch: CampaignSearch;
  highlightTab: string | { plant: string; tab: string };
  setHighlightedTab: (tab: string | { plant: string; tab: string }) => void;
  urlsNotLoaded: string[];
  setURLsNotLoaded: (urls: string[]) => void;
  settingsInfo: User;
  setSettingsInfo: (settings: UserSettings) => void;
};

const FEEDBACK_TAB = 'feedback' as const;
const FEEDBACK_SECTION = 'feedback' as const;
const QUERY_SEARCH_PARAM = 'search' as const;
const QUERY_CAMPAIGN_PARAM = 'campaign' as const;
const VAN_FEEDBACK_PREFIX = 'van_' as const;
const CAMPAIGNS_PATH = 'campaigns[' as const;
const CAMPAIGN_MANAGER_ENTRY_SECTION = 'campaignManagerEntry' as const;
const CAMPAIGN_NUMBER_FIELD = 'campaignNumber' as const;
const CAMPAIGN_TITLE_FIELD = 'campaignTitle' as const;
const CAMPAIGN_STATE_FIELD = 'campaignState' as const;
const CAMPAIGN_CLASSIFICATIONS_FIELD = 'campaignClassifications' as const;
const ADDITIONAL_INFORMATION_ABOUT_CASE_SECTION = 'additionalInformationAboutTheCase' as const;
const CLASSIFICATION_FIELD = 'classification' as const;

const intaktFields = [`${ADDITIONAL_INFORMATION_ABOUT_CASE_SECTION}.${CAMPAIGN_STATE_FIELD}`];
const Feedback: React.FC<FeedbackProps> = (props) => {
  const { setFeedback, originalValues, setOriginalValues, subCampaignInfo, subCampaignFeedback,
    setHelpArea, isVANCR, validations, campaignSearch, highlightTab, setHighlightedTab, urlsNotLoaded, setURLsNotLoaded, settingsInfo, setSettingsInfo } = props;
  const { id, version } = useParams<{ id?: string; version?: string }>();

  const theme = useTheme();
  const { t } = useTranslation();
  const checkDisabled = useCheckDisabled(true, true);
  const lang = useAppLanguage();
  const { addToast } = useToastContext();
  const { handleCriticalError } = useError();
  const navigate = useNavigate();
  const validateSubCampaignForm = useSubCampaignValidate(false);
  const [searchSubCRURL, crSelectedURL] = [useQuery().get(QUERY_SEARCH_PARAM), useQuery().get(QUERY_CAMPAIGN_PARAM)];
  const classes = useStyles();
  const authService = useAuth();

  const [subCampaignForms, setSubCampaignForms] = useState<ReduxFormsState | null>(null);
  const isLoading = !subCampaignForms?.classifications || !subCampaignFeedback?.id;
  const [CNAlrreadyExists, setCNAlrreadyExists] = useState<{ crSelected: number; sameCN: boolean } | null>(null);
  const [showConfirmDeleteCR, setShowConfirmDeleteCR] = useState<number | null>(null);
  const [crSelected, setCRSelected] = useState(0);
  const [isCaRTZASMLinkDisabled, setIsCaRTZASMLinkDisabled] = useState(true);
  const [fisrtURLLoaded, setFirstURLLoaded] = useState(false);
  const [isAutoSaveActive, setIsAutoSaveActive] = useState(false);
  const [originalCampaignsCount, setOriginalCampaignsCount] = useState(0);
  const campaignSelected = useMemo(() => subCampaignFeedback?.campaigns[crSelected] || subCampaignFeedback?.campaigns[0],
    [subCampaignFeedback, crSelected]);
  const linkCampaignToZASM = useApiRequest(campaignServices.linkCampaignToZASM);
  const checkZASMCampaignExists = useApiRequest(campaignServices.checkZASMCampaignExists);
  const putAIQualityFlag = useApiRequest(campaignServices.putAIQualityFlag);
  const campaignNumber = subCampaignFeedback?.campaigns?.[crSelected]?.campaignManagerEntry?.campaignNumber;

  const campaignStateValue = (subCampaignForms?.state as FeedbackCustomForm)?.options
    ?.find((state) => state.enumAPI === campaignSelected?.campaignManagerEntry?.campaignState)
    ?? getZasmCampaignState(campaignSelected?.campaignManagerEntry?.campaignState);

  const checkDisabledReason = (field: string, isIntaktField: boolean = false, isCampaignNumber: boolean = false) => {
    if ((isCampaignNumber && campaignSelected?.zasmIntaktSynchronization?.updatedByZASM) || (campaignSelected?.zasmIntaktSynchronization?.updatedByZASM
      && campaignSelected?.zasmIntaktSynchronization?.zasmFieldsSynchronized.includes(field) && !isIntaktField)) {
      return 'DISABLED_BY_ZASM';
    }

    if (campaignSelected?.zasmIntaktSynchronization?.updatedByINTAKT
      && campaignSelected?.zasmIntaktSynchronization?.intaktFieldsSynchronized.includes(field)) {
      return 'DISABLED_BY_INTAKT';
    }

    if (!checkDisabled && (campaignSelected?.campaignManagerEntry?.campaignNumber === '')) {
      return 'ENABLED';
    }

    if (checkDisabled) {
      return 'DISABLED_BY_READ_ONLY_MODE';
    }

    return 'ENABLED';
  };

  const checkIsDisabledField = (field: string) => checkDisabledReason(field, intaktFields.includes(field)) !== 'ENABLED';

  const getLabelForPossibleDisabledInput = (label: string, field: string, isCampaignNumber: boolean = false): ReactNode | string => {
    const checkDisabledReasonValue = checkDisabledReason(field, intaktFields.includes(field), isCampaignNumber);
    const noInfoNeededReasons = ['DISABLED_BY_CAMPAIGN_NUMBER_EMPTY', 'DISABLED_BY_READ_ONLY_MODE', 'ENABLED'];

    if (noInfoNeededReasons.includes(checkDisabledReasonValue)) return t(label);

    const isZASMLabel = checkDisabledReasonValue === 'DISABLED_BY_ZASM' || isCampaignNumber;

    return (
      <>
        {t(label)}
        <TooltipComponent
          classes={{ tooltip: styles.TooltipFreeText }}
          arrow
          title={(<Typography variant="body2" component="span">{t(`feedback.${checkDisabledReasonValue}`)}</Typography>)}
        >
          <span className={styles.InfoIconContainer}>
            <TabLabelIcon isProvidedField />
            <div className={isZASMLabel ? styles.ZASMLogoContainer : styles.INTAKTLogoContainer}>
              <img
                className={isZASMLabel ? styles.ZASMLogoLabel : styles.INTAKTLogoLabel}
                src={isZASMLabel ? ZASMLogo : INTAKTLogo}
                alt={isZASMLabel ? 'ZASM Logo' : 'INTAKT Logo'}
              />
            </div>
          </span>
        </TooltipComponent>
      </>
    );
  };

  const createZASMDisabled = campaignSelected?.zasmIntaktSynchronization?.updatedByZASM
    || campaignSelected?.campaignManagerEntry?.campaignNumber !== '' || checkDisabled || isLoading;

  const zasmButtonsVisible = !isVANCR ? subCampaignInfo?.workflowType === WORKFLOW_TYPE.DIGITAL : subCampaignInfo?.workflowType === WORKFLOW_TYPE.NOT_READY;

  const validateSubCampaignFormCheck = checkDisabled ? () => { } : validateSubCampaignForm;

  useEffect(() => {
    if (!settingsInfo && Object.keys(settingsInfo).length === 0) {
      usersServices.getUsersSettings(authService.userCode)
        .then((settingsData) => {
          setSettingsInfo(settingsData);
        })
        .catch((error) => {
          setSettingsInfo({});
          addToast(error.message, 'Error');
        });
    }
  }, [settingsInfo, setSettingsInfo, authService.userCode]);// eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (subCampaignForms?.classifications && crSelectedURL && !fisrtURLLoaded) {
      const crSelectedURLNumber = subCampaignFeedback.campaigns.findLastIndex((campaign) => campaign.campaignManagerEntry?.campaignNumber === crSelectedURL);
      onCRSelectedChange(crSelectedURLNumber === -1 ? 0 : crSelectedURLNumber);
      setFirstURLLoaded(true);
    }
  }, [crSelectedURL, subCampaignFeedback]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!campaignSearch?.active) {
      setHighlightedTab('');
    }
  }, [campaignSearch]);

  useEffect(() => {
    campaignServices.getSubCampaignFeedbackForms()
      .then((res) => {
        // from object to array of objects with 3 keys, en, de, and enumAPI
        res.form.state.options = Object.entries(res.form.state.options).map(([enumAPI, object]) => ({ enumAPI, ...(object as object) }));
        res.form.stateAdditionalInfo.options = Object.entries(res.form.stateAdditionalInfo.options)
          .map(([enumAPI, object]) => ({ enumAPI, ...(object as object) }));
        res.form.classificationsAdditionalInfo.options = Object.entries(res.form.classificationsAdditionalInfo.options)
          .map(([enumAPI, object]) => ({ enumAPI, ...(object as object) }));

        setSubCampaignForms(res.form);
      }).catch(handleCriticalError);

    campaignServices.getSubCampaign(id, `${isVANCR ? VAN_FEEDBACK_PREFIX : ''}${FEEDBACK_TAB}`, version)
      .then((data) => {
        setFeedback(data?.module);
        setOriginalCampaignsCount(data?.module?.campaigns?.length || 0);
      })
      .catch(handleCriticalError);
  }, [campaignSearch?.active]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const timeEntries = campaignSelected?.time || {};

    const total = Object.keys(timeEntries)
      .filter((key) => key !== 'total')
      .reduce((acc, cur) => {
        const value = timeEntries[cur];

        return acc + (!value || value < 0 ? 0 : value);
      }, 0);

    if (campaignSelected?.time?.total !== total) {
      const newState = {
        ...campaignSelected?.time,
        total,
      };
      setFeedback({
        ...subCampaignFeedback,
        campaigns: subCampaignFeedback.campaigns.map((cr, i) => (i === crSelected
          ? {
            ...cr,
            time: newState,
          }
          : cr)),
      });
    }
  }, [campaignSelected?.time]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSetOriginValues = (changes: boolean = true) => {
    const values = {
      ...originalValues,
      hasChange: changes,
    };
    setOriginalValues(values);
  };

  const handleChangeClassification = (allClassification: string[]) => {
    const newState = {
      ...subCampaignFeedback,
      campaigns: subCampaignFeedback.campaigns.map((cr, i) => (i === crSelected
        ? {
          ...cr,
          campaignManagerEntry: {
            ...cr.campaignManagerEntry,
            campaignClassifications: allClassification,
          },
        }
        : cr)),
    };
    setFeedback(newState);
    onSetOriginValues();
  };

  const selectClassificationMenu = (optionSelected: string[]) => {
    if (optionSelected.length > 8) {
      addToast(t('feedback.classificationLimit'), 'Warning');

      return;
    }
    handleChangeClassification(optionSelected);
  };

  const handleDelete = (chipToDelete: string) => {
    handleChangeClassification(campaignSelected?.campaignManagerEntry?.campaignClassifications
      .filter((chip) => chip !== chipToDelete));
  };

  const checkCampaignNumberAlreadyExists = (value: string) => {
    let cNExists = false;

    if (value.length === 12) {
      const campaignNumberExists = subCampaignFeedback.campaigns.some((cr) => cr.campaignManagerEntry.campaignNumber === value);

      if (campaignNumberExists) {
        addToast(t('feedback.campaignNumberAlreadyExists'), 'Warning', 'SAME_CAMPAIGN');
        cNExists = true;
      }
    }

    return { crSelected, sameCN: cNExists };
  };

  const handleChangeIntegerField = useCallback((label: string, value: string, section: string, isSyncField: boolean = false, index: number, isCalculated: boolean = false) => {
    if (fieldIsValid(label, value)) {
      let newState = {
        ...subCampaignFeedback,
        campaigns: subCampaignFeedback.campaigns.map((cr, i) => (i === index
          ? {
            ...cr,
            [section]: {
              ...cr?.[section],
              [label]: parseInt(value, 10) < 0 || value === '' ? -1 : +value,
            },
          }
          : cr)),
      };

      if (label === 'campaignNumber') {
        setCNAlrreadyExists(checkCampaignNumberAlreadyExists(value));
        newState = {
          ...newState,
          campaigns: subCampaignFeedback.campaigns.map((cr, i) => (i === index
            ? {
              ...cr,
              campaignManagerEntry: {
                ...cr.campaignManagerEntry,
                [label]: `${value}`,
              },
            }
            : cr)),
        };
      }
      setFeedback(newState);

      if (!isCalculated) onSetOriginValues();
    }
  }, [subCampaignFeedback?.campaigns]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (validations?.[subCampaignInfo.plantLanguage]?.campaigns?.[crSelected]?.campaignNumber?.invalid) {
      validateSubCampaignFormCheck();
    }
  }, [subCampaignFeedback?.campaigns[crSelected]?.campaignManagerEntry?.campaignNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChangeText = useCallback((
    label: string,
    value: string,
    section: string | null,
    isSyncField = false,
    noCampaignField = false,
  ) => {
    void isSyncField;
    const isClassificationAddiionalInfo = section === ADDITIONAL_INFORMATION_ABOUT_CASE_SECTION && label === CLASSIFICATION_FIELD;

    if (section && !noCampaignField) {
      setFeedback({
        ...subCampaignFeedback,
        campaigns: subCampaignFeedback.campaigns.map((cr, i) => (i === crSelected
          ? {
            ...cr,
            [section]: {
              ...cr[section],
              [label]: isClassificationAddiionalInfo ? [value] : value,
            },
          }
          : cr)),
      });
    } else if (section && noCampaignField) {
      setFeedback({
        ...subCampaignFeedback,
        [section]: {
          ...(subCampaignFeedback[section] as Record<string, unknown>),
          [label]: isClassificationAddiionalInfo ? [value] : value,
        },
      });
    } else if (!section && noCampaignField) {
      setFeedback({
        ...subCampaignFeedback,
        [label]: value,
      });
    }

    onSetOriginValues();
  }, [subCampaignFeedback, crSelected]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (highlightTab) {
      const findIndexTab = subCampaignFeedback.campaigns.findIndex(
        (campaign) => campaign?.campaignManagerEntry?.campaignNumber === highlightTab,
      );

      if (typeof findIndexTab === 'number' && findIndexTab !== -1) {
        onCRSelectedChange(findIndexTab);
      }
    }
  }, [highlightTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const onCRSelectedChange = (index: number, fromCreation = false) => {
    if (!subCampaignFeedback?.campaigns?.[index]?.campaignManagerEntry && !fromCreation) return;
    const currentCRNumber = subCampaignFeedback.campaigns[index]?.campaignManagerEntry?.campaignNumber ?? '';
    navigate({
      search: `?campaign=${currentCRNumber}${searchSubCRURL ? `&search=${searchSubCRURL}` : ''}`,
    });
    setCRSelected(index);
  };

  const onAddNewCampaign = async () => {
    if (subCampaignFeedback.campaigns.some((cr) => cr.campaignManagerEntry.campaignNumber === '')) { // check if empty campaign exists
      addToast(t('feedback.campaignNumberEmpty'), 'Warning');

      return;
    }
    setFeedback({
      ...subCampaignFeedback,
      campaigns: [...subCampaignFeedback.campaigns, { ...EmptySubCRCampaign as unknown as CampaignOfFeedbackSubCR }],
    });
    onSetOriginValues();
    onCRSelectedChange(subCampaignFeedback.campaigns.length, true);
    setCNAlrreadyExists(null);
    validateSubCampaignFormCheck();
  };

  const onConfirmDeleteCampaign = async (index: number) => {
    onCRSelectedChange(((index - 1) < 0) ? 0 : index - 1);
    const campaigns = subCampaignFeedback.campaigns.filter((cr, i) => i !== index);
    setFeedback({ ...subCampaignFeedback, campaigns });
    onSetOriginValues();
    setShowConfirmDeleteCR(null);
    setCNAlrreadyExists(null);
    validateSubCampaignFormCheck();
  };

  const onDeleteCampaign = (index: number) => {
    if (subCampaignFeedback.campaigns[index].campaignManagerEntry.campaignNumber === '') {
      onConfirmDeleteCampaign(index);

      return;
    }
    setShowConfirmDeleteCR(index);
  };

  useEffect(() => {
    async function setCaRTZASMLinkVisibility() {
      try {
        const ZASMIdFound = await checkZASMCampaignExists(campaignNumber);
        setIsCaRTZASMLinkDisabled(!ZASMIdFound);
      } catch (error) {
        setIsCaRTZASMLinkDisabled(true);

        if (error instanceof Error && (error as { code?: string }).code === errorTypes.ZASM_SERVER_ERROR) {
          addToast(t('errors.createZASMCrError'), 'Error');

          return;
        }

        if (error instanceof Error) {
          addToast(error.message, 'ERROR');
        }
      }
    };

    if (campaignNumber?.length !== 12 || checkDisabled || isLoading || campaignSelected?.zasmIntaktSynchronization?.updatedByZASM) {
      setIsCaRTZASMLinkDisabled(true);

      return;
    }

    setCaRTZASMLinkVisibility();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignNumber, checkDisabled, isLoading, addToast, campaignSelected?.zasmIntaktSynchronization?.updatedByZASM]);

  const onLinkDataFromZASM = async () => {
    if (originalValues.hasChange) {
      setFeedback({
        ...subCampaignFeedback,
        saveNeeded: true,
      });
      setIsAutoSaveActive(true);

      return;
    }
    await setDataFromZASM();
  };

  const setDataFromZASM = useCallback(async () => {
    try {
      await linkCampaignToZASM({ cartId: id, campaignId: campaignNumber });
      const updatedData = await campaignServices.getSubCampaign(id, `${isVANCR ? VAN_FEEDBACK_PREFIX : ''}${FEEDBACK_TAB}`, version);
      setFeedback(updatedData?.module);
      setOriginalCampaignsCount(updatedData?.module?.campaigns?.length || 0);
      addToast(t('feedback.linkZASMSuccess'), 'Success');
    } catch (error) {
      if (error instanceof Error && (error as { code?: string }).code === errorTypes.ZASM_SYNC_CAMPAIGN_ERROR) {
        addToast(t('feedback.linkZASMError'), 'Error');

        return;
      }
      handleCriticalError(error);
    }
  }, [campaignNumber, id, isVANCR, handleCriticalError, setFeedback, addToast, t, version, linkCampaignToZASM]);

  useEffect(() => {
    async function linkDataFromZasm() {
      await setDataFromZASM();
    }

    if (!originalValues.hasChange && isAutoSaveActive && subCampaignFeedback?.saveNeeded) {
      delete subCampaignFeedback.saveNeeded;
      setIsAutoSaveActive(false);
      linkDataFromZasm();
    }
  }, [isAutoSaveActive, originalValues?.hasChange, subCampaignFeedback?.saveNeeded, setDataFromZASM]);

  useEffect(() => {
    if (subCampaignFeedback?.campaigns && !subCampaignFeedback?.campaigns?.[crSelected]) {
      onCRSelectedChange(0);
    }
  }, [subCampaignFeedback?.campaigns]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleFieldSelection = async () => {
      if (campaignSearch?.fieldSelected && campaignSearch?.fieldSelected.tab === FEEDBACK_TAB
        && campaignSearch?.fieldSelected.field.path?.includes('campaigns[')) {
        await new Promise((resolve) => setTimeout(resolve, 200));
        const tab = getTabIndex(campaignSearch.fieldSelected.field.path?.split('.')[0]);
        onCRSelectedChange(parseInt(tab, 10));
      } else {
        onCRSelectedChange(0);
      }
    };

    handleFieldSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignSearch?.fieldSelected]);

  useEffect(() => {
    const handleCoincidenceFieldScroll = async () => {
      if (campaignSearch?.selectedCoincidenceFieldId && campaignSearch.fieldSelected?.field.path?.includes(CAMPAIGNS_PATH)) {
        await new Promise((resolve) => setInterval(() => { // wait until the field is rendered
          if (document.getElementById(`child-container-campaign-${crSelected}`)) resolve(1);
        }, 200));
        scrollToField(`child-container-campaign-${crSelected}`, campaignSearch.selectedCoincidenceFieldId, true);
      } else if (campaignSearch?.selectedCoincidenceFieldId && !campaignSearch.fieldSelected?.field.path?.includes(CAMPAIGNS_PATH)) {
        scrollToField('subCampaignDetailContent', campaignSearch?.selectedCoincidenceFieldId);
      }
    };

    handleCoincidenceFieldScroll();
  }, [campaignSearch?.selectedCoincidenceFieldId]);

  const handleToggleAIQuality = async (newValue: boolean) => {
    try {
      await putAIQualityFlag(id, subCampaignFeedback.version, newValue);
      setFeedback({
        ...subCampaignFeedback,
        aiQuality: newValue,
      });
      addToast(t(newValue ? 'feedback.aiQualityMarked' : 'feedback.aiQualityUnmarked'), 'Success');
    } catch (error) {
      if (error instanceof Error) {
        addToast(error.message, 'Error');
      }
    }
  };

  const slideRenderer = useCallback(({ index, key }) => {
    if (index !== crSelected) return <div role="tabpanel" dir={theme.direction} className={styles.CampaignView} key={key}><SubCampaignCRSkeleton isDigital /></div>;
    const campaign = subCampaignFeedback.campaigns[index];
    const { campaignZASMURL, selectionZASMURL, campaignINTAKTURL } = buildExternalUrls(campaign?.zasmIntaktSynchronization?.zasmId, subCampaignInfo, campaign?.campaignManagerEntry.campaignNumber);

    return (
      <div role="tabpanel" dir={theme.direction} className={styles.CampaignView} key={key} id={`child-container-campaign-${index}`}>
        <TitleWithDate title={t('feedback.title', { plant: subCampaignFeedback.plantName })} date={campaign?.zasmIntaktSynchronization?.lastZASMUpdateDate} />
        <div className="w-100" data-testid={`feedback-campaign-manager-entry-section-${index}`}>
          <Grid container spacing={2} rowSpacing={1} sx={{ marginBottom: '16px' }} columnSpacing={1}>
            <Grid item xs={12} sm={6} md={3}>
              <InputText
                className={styles.CampaignNumber}
                disabled={(campaign?.zasmIntaktSynchronization?.updatedByZASM || campaign?.zasmIntaktSynchronization?.updatedByINTAKT) || checkDisabled}
                id={`input-campaign-number-feedback-${index}`}
                label={getLabelForPossibleDisabledInput('feedback.campaignNumber', 'campaignManagerEntry.campaignNumber', true)}
                noTranslationField
                type="number"
                handleChange={(ev) => handleChangeIntegerField(CAMPAIGN_NUMBER_FIELD, ev.target.value, CAMPAIGN_MANAGER_ENTRY_SECTION, false, index)}
                placeholder={`${t('feedback.numberMessage')}`}
                fieldPath={[FEEDBACK_SECTION, `campaigns[${index}]`, CAMPAIGN_MANAGER_ENTRY_SECTION, CAMPAIGN_NUMBER_FIELD]}
                value={campaignNumber || ''}
                invalid={((index === CNAlrreadyExists?.crSelected && CNAlrreadyExists?.sameCN))
                  || validations?.[subCampaignInfo.plantLanguage]?.campaigns?.[index]?.campaignNumber?.invalid
                  || validations?.[subCampaignInfo.plantLanguage]?.campaigns?.[index]?.campaignNumber?.min}
                helperText={[
                  ...(((index === CNAlrreadyExists?.crSelected && CNAlrreadyExists?.sameCN)
                    || validations?.[subCampaignInfo.plantLanguage]?.campaigns?.[index]?.campaignNumber?.errors?.duplicate)
                    ? [t('feedback.campaignNumberAlreadyExists')]
                    : []),
                  ...(validations?.[subCampaignInfo.plantLanguage]?.campaigns?.[index]?.campaignNumber?.errors?.required
                    ? [t('validations.required')]
                    : []),
                  ...(validations?.[subCampaignInfo.plantLanguage]?.campaigns?.[index]?.campaignNumber?.errors?.min
                    ? [t('feedback.commentsMinLength')]
                    : []),
                ].filter((item, index) => (index === 0))}
              />
            </Grid>
            <Grid item container xs={12} sm={6} md={2} direction="column" justifyContent="center" alignItems="center">
              <Button
                btnText={t('feedback.zasmConnect')}
                buttonStyle="PrimaryRound"
                disabled={isCaRTZASMLinkDisabled}
                handleClick={onLinkDataFromZASM}
              />
            </Grid>
            {zasmButtonsVisible && (
              <>
                <Grid item container xs={12} sm={6} md={3} direction="column" justifyContent="center" alignItems="center" gap={1}>
                  <ZasmConnectionButton
                    index={index}
                    id={id ?? ''}
                    createZASMDisabled={createZASMDisabled}
                    isSelection={false}
                    version={version ?? ''}
                    setURLsNotLoaded={setURLsNotLoaded}
                    setOriginalCampaignsCount={setOriginalCampaignsCount}
                  />
                  {campaign?.zasmIntaktSynchronization?.zasmId && (
                    <NormalA href={campaignZASMURL}>{t('feedback.openZASMCampaign')}</NormalA>
                  )}
                </Grid>

                <Grid item container xs={12} sm={6} md={4} direction="column" justifyContent="center" alignItems="center" gap={1}>
                  <ZasmConnectionButton
                    index={index}
                    id={id ?? ''}
                    createZASMDisabled={createZASMDisabled}
                    isSelection
                    version={version ?? ''}
                    setURLsNotLoaded={setURLsNotLoaded}
                    setOriginalCampaignsCount={setOriginalCampaignsCount}
                  />
                  {campaign?.zasmIntaktSynchronization?.zasmId && (
                    <NormalA href={selectionZASMURL}>{t('feedback.openZASMSelection')}</NormalA>
                  )}
                </Grid>
              </>
            )}
          </Grid>
          <Grid container spacing={2} rowSpacing={1} sx={{ marginBottom: '16px' }} columnSpacing={1}>
            <Grid item xs={12} sm={6} md={3}>
              <InputText
                id={`input-campaign-title-feedback-${index}`}
                label={getLabelForPossibleDisabledInput('feedback.campaignTitle', 'campaignManagerEntry.campaignTitle')}
                handleChange={(ev) => handleChangeText(CAMPAIGN_TITLE_FIELD, ev.target.value, CAMPAIGN_MANAGER_ENTRY_SECTION)}
                fieldPath={[FEEDBACK_SECTION, `campaigns[${index}]`, CAMPAIGN_MANAGER_ENTRY_SECTION, CAMPAIGN_TITLE_FIELD]}
                noTranslationField
                value={campaign?.campaignManagerEntry?.campaignTitle || ''}
                className="w-100"
                disabled={checkIsDisabledField('campaignManagerEntry.campaignTitle')}
                maxLength={1024}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                key={`campaignStateAutocomplete-${lang}`}
                id={`input-campaign-state-feedback-${index}`}
                data-testid={`input-campaign-state-feedback-${index}`}
                value={campaignStateValue}
                options={(subCampaignForms?.state as FeedbackCustomForm)?.options ?? []}
                onChange={(ev, value) => handleChangeText(CAMPAIGN_STATE_FIELD, value?.enumAPI ?? '', CAMPAIGN_MANAGER_ENTRY_SECTION)}
                isOptionEqualToValue={(optionToTest, currentValue) => optionToTest?.enumAPI === currentValue?.enumAPI}
                getOptionLabel={(option) => option?.[lang] ?? ''}
                renderInput={(params) => (
                  <HighlightMask
                    fieldId={`input-campaign-state-feedback-${index}-text`}
                    fieldPath={[FEEDBACK_SECTION, `campaigns[${index}]`, CAMPAIGN_MANAGER_ENTRY_SECTION, CAMPAIGN_STATE_FIELD]}
                    value={campaignStateValue?.[lang]}
                    fieldType={fieldTypes.DROPDOWN}
                    disabled={checkIsDisabledField('campaignManagerEntry.campaignState')}
                    noTranslationField
                  >
                    <TextField
                      {...params}
                      label={getLabelForPossibleDisabledInput('feedback.state', 'campaignManagerEntry.campaignState')}
                      variant="outlined"
                      disabled={checkIsDisabledField('campaignManagerEntry.campaignState')}
                    />
                  </HighlightMask>
                )}
                classes={checkIsDisabledField('campaignManagerEntry.campaignState') ? { inputRoot: classes.disableInputRoot } : {}}
                disabled={checkIsDisabledField('campaignManagerEntry.campaignState')}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} rowSpacing={1} sx={{ marginBottom: '16px' }} columnSpacing={1}>
            <Grid item xs={12} sm={6} md={6}>
              <MultiSelection
                id={`select-classification-${index}`}
                fieldPath={[FEEDBACK_SECTION, `campaigns[${index}]`, CAMPAIGN_MANAGER_ENTRY_SECTION, CAMPAIGN_CLASSIFICATIONS_FIELD]}
                handleDeleteItem={handleDelete}
                label={getLabelForPossibleDisabledInput('feedback.classification', 'campaignManagerEntry.campaignClassifications')}
                options={(subCampaignForms?.classifications as ReduxForms)?.options}
                value={campaign?.campaignManagerEntry?.campaignClassifications || []}
                handleChange={(ev: SelectChangeEvent<any>) => selectClassificationMenu(ev.target.value)}
                formControlClass={styles.fullWidth}
                language={lang}
                disabled={checkIsDisabledField('campaignManagerEntry.campaignClassifications')}
              />
            </Grid>
          </Grid>
        </div>
        <AditionalInfoSection
          subCampaignForms={subCampaignForms}
          handleChangeText={handleChangeText}
          checkIsDisabledField={checkIsDisabledField}
          getLabelForPossibleDisabledInput={getLabelForPossibleDisabledInput}
          classes={classes}
          index={index}
          checkDisabled={checkDisabled}
          campaignINTAKTURL={campaignINTAKTURL}
          setURLsNotLoaded={setURLsNotLoaded}
        />
        <FeedbackVehiclesTable campaignNumber={campaignNumber} />
        <TimeBillingSection
          handleChangeIntegerField={handleChangeIntegerField}
          handleChangeText={handleChangeText}
          checkIsDisabledField={checkIsDisabledField}
          getLabelForPossibleDisabledInput={getLabelForPossibleDisabledInput}
          index={index}
        />
      </div>
    );
  }, [subCampaignForms, campaignSelected, crSelected, validations?.[subCampaignInfo.plantLanguage]?.campaigns, campaignNumber, isCaRTZASMLinkDisabled]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="w-100" id="subcampaign-feedback">
      {isLoading && <Spinner isVisible isMask />}
      {!isLoading && (
        <>
          <AccordionPanel
            title={(
              <TabsCampaignsSubCR
                tabsLabelsConfig={subCampaignFeedback.campaigns.map((campaign, index) => ({
                  label: campaign.campaignManagerEntry.campaignNumber,
                  deleteDisabled: campaign?.zasmIntaktSynchronization?.updatedByZASM,
                  invalid: validations?.[subCampaignInfo.plantLanguage]?.campaigns?.[index]?.invalid || false,
                  status: campaign.campaignManagerEntry.campaignState,
                }))}
                onAddNewTab={onAddNewCampaign}
                onDeleteTab={onDeleteCampaign}
                setTabSelected={onCRSelectedChange}
                crSelected={crSelected}
                maxTabs={10}
                readOnly={checkDisabled}
                originalCampaignsCount={originalCampaignsCount}
              />
            )}
            headerCustomClass={styles.AccordionHeaderCustom}
            rightConentCustomClass={styles.RightConentCustomClass}
            handleClick={() => setHelpArea(HELP_AREA.MANAGER_ENTRY)}
            tabsAccordion
            noExpandedButton
          >
            <VirtualizeSwipeableViews
              axis={theme.direction === 'rtl' ? 'x-reverse' : 'x'}
              index={crSelected}
              onChangeIndex={onCRSelectedChange}
              slideRenderer={slideRenderer}
              overscanSlideAfter={subCampaignFeedback.campaigns.length}
              overscanSlideBefore={subCampaignFeedback.campaigns.length}
              slideCount={subCampaignFeedback.campaigns.length}
              containerStyle={{ // fix for first animation https://github.com/oliviertassinari/react-swipeable-views/issues/599
                transition: 'transform 0.95s cubic-bezier(0.15, 0.3, 0.25, 1) 0s',
              }}
              slideStyle={{ maxHeight: '50vh', overflowX: 'hidden', margin: '5px 0px 5px 0px' }}
            />
          </AccordionPanel>
          <AccordionPanel
            id="feedback-ai-quality-section"
            title={t('feedback.aiQualityLabel')}
            noExpandedButton
            isOpen={true}
          >
            <Switch
              checked={!!subCampaignFeedback?.aiQuality}
              onChange={() => handleToggleAIQuality(!subCampaignFeedback?.aiQuality)}
              color="primary"
            />
          </AccordionPanel>
          <ApproverCommentSection handleChangeText={handleChangeText} />
        </>
      )}
      {showConfirmDeleteCR !== null && (
        <ConfirmDialog
          close={() => setShowConfirmDeleteCR(null)}
          confirm={() => onConfirmDeleteCampaign(showConfirmDeleteCR)}
          title={t('dialog.deleteCampaignSubCR.title')}
          message={t('dialog.deleteCampaignSubCR.message', { crNumber: subCampaignFeedback.campaigns?.[showConfirmDeleteCR]?.campaignManagerEntry?.campaignNumber })}
          closeBtnText={t('dialog.cancel')}
          confirmBtnText={t('dialog.confirm')}
        />
      )}
      {urlsNotLoaded.length > 0 && (
        <ConfirmDialog
          close={() => setURLsNotLoaded([])}
          title={t(`dialog.${isZASMUrls(urlsNotLoaded) ? 'urlZasmNotLoaded' : 'urlINTAKTNotLoaded'}.${urlsNotLoaded.length > 1 ? 'title' : 'titleSingular'}`)}
          message={(
            <>
              <h4>{t(`dialog.${isZASMUrls(urlsNotLoaded) ? 'urlZasmNotLoaded' : 'urlINTAKTNotLoaded'}.${urlsNotLoaded.length > 1 ? 'message' : 'messageSingular'}`)}</h4>
              {urlsNotLoaded.map((url) => <><NormalA key={url} href={url}>{url}</NormalA><br /><br /></>)}
            </>
          )}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  subCampaignFeedback: getSubCampaignFeedback(state),
  originalValues: getSubCampaignOriginalValues(state),
  subCampaignInfo: getSubCampaignInfo(state),
  settingsInfo: getSettingsInfo(state),
  isVANCR: getIsVANCR(state),
  validations: getFeedbackValidations(state),
  campaignSearch: getCampaignSearch(state),
  highlightTab: getHighlightTab(state),
});

const actionCreators = {
  setFeedback: setSubCampaignFeedback,
  setOriginalValues: setSubCampaignOriginalValues,
  setHelpArea: setAreaHelp,
  setValidations: setFeedbackValidations,
  setHighlightedTab: setHighlightedTab,
  setSettingsInfo: setSettingsInfo,
};

export default (connect(mapStateToProps, actionCreators)(Feedback));
